import { type ActionCardData, getActiveTagNames, WidgetType } from '@playbooks/action-cards-utils/src'

import { getNode, traverseNodes } from '../actionCardOperations'
import { adjustActionCardForPrompt } from './adjustActionCardForPrompt'
import { WidgetContext } from '../../features/widgets/widgetTypes'

/** Options for the processPromptReplacements function */
interface ProcessPromptReplacementsOptions {
  /** The prompt to be adjusted */
  prompt: string
  /** The Action Card in which context the prompt is generated */
  actionCardsData: ActionCardData
  /** The context of where the prompt is used */
  context?: WidgetContext
}

/** Placeholder replacement */
interface Replacement {
  /** The placeholder to be replaced */
  placeholder: string
  /** The actual value to replace the placeholder with */
  value: string
}

/**
 * Adjust the AI prompt before sending it to the AI model
 * by replacing special placeholders with the actual values.
 * @param prompt The prompt to be adjusted
 * @param actionCardsData The Action Card in which context the prompt is generated
 * @returns The adjusted prompt
 */
export default async function processPromptReplacements({
  prompt,
  actionCardsData,
  context,
}: ProcessPromptReplacementsOptions) {
  const preparedActionCard = adjustActionCardForPrompt(actionCardsData.sections)
  const section = adjustActionCardForPrompt(context?.section)
  const headerScopeSections = adjustActionCardForPrompt(context?.headerScopeSections)

  // Find the placeholders in the prompt to be replaced with the actual values
  let allReplacements: Replacement[] = [
    // Replacement for the Action Card JSON
    {
      placeholder: '[actionCard]',
      value: JSON.stringify(preparedActionCard),
    },
    // Replacement for the current Action Card node where the widget is rendered (#10482)
    {
      placeholder: '[context]',
      value: context?.originalText || '',
    },
    {
      placeholder: '[currentSection]',
      value: JSON.stringify(section || {}),
    },
    {
      placeholder: '[currentHeader]',
      value: JSON.stringify(headerScopeSections || []),
    },
    {
      placeholder: '[transcript]',
      value: getFirstTeammateTranscript(actionCardsData),
    },
    // Replacements for specific answers in the Action Card,
    // e.g. [answer#123] will be replaced with "value" prop of the question with ID 123 (#7852)
    ...[...prompt.matchAll(/\[answer\#(\d+)\]/g)].map(([placeholder, id]) => ({
      placeholder,
      value: getNode(actionCardsData.sections, id)?.value,
    })),
  ]

  // Replace the placeholders in the prompt with the actual values
  allReplacements.forEach(r => {
    prompt = prompt.replaceAll(r.placeholder, r.value || '')
  })

  // Process `%if[ condition ] ... endif%` conditional blocks (#11433)
  const activeTags = getActiveTagNames(actionCardsData)
  prompt = processConditionalReplacements(prompt, activeTags || [])

  return prompt
}

/**
 * Find the first AiTeammate widget in the Action Card with a transcript and return its transcript.
 */
function getFirstTeammateTranscript(actionCardsData: ActionCardData): string {
  let transcript = ''

  traverseNodes(actionCardsData.sections, '*', (obj, key) => {
    if (transcript) return

    const value = obj[key]
    if (
      value?.type === WidgetType.AiTeammate &&
      typeof value.value === 'object' &&
      typeof value.value?.transcript === 'string' &&
      value.value.transcript.trim()
    ) {
      transcript = value.value.transcript
    }
  })

  return transcript
}

/**
 * Replace every `%if[ condition ] ... endif%` block in the given `prompt`.
 *
 * Supported operators — evaluated strictly **left → right** (no precedence):
 *
 *   • `&&`   logical AND
 *
 *   • `||`   logical OR
 *
 *   • `!`    unary NOT (immediately before a tag, e.g. `!beta`)
 *
 * Examples
 *
 *   • `tag1&&!tag2`      → true if tag1 **is** selected **and** tag2 is **not**
 *
 *   • `!tag1||tag2`      → true if tag1 is not selected **or** tag2 is
 *
 * @param prompt String that may contain conditional blocks.
 * @param activeTags Tags currently activated in the Action Card.
 * @returns Prompt with all conditional sections resolved.
 * @story User Story 11433: Conditions for AI prompts in AC widget
 */
function processConditionalReplacements(prompt: string, activeTags: string[]): string {
  // Convert active tags to a Set for fast lookup
  const tagSet = new Set(activeTags.map(t => t.trim()))

  /** Evaluate one condition string such as "tag1&&!tag2||tag3" */
  function evalCondition(condition: string): boolean {
    // Split into alternating [operand, operator, operand, ...]
    const tokens = condition
      .trim()
      .split(/(&&|\|\|)/) // keep the operators
      .map(t => t.trim())
      .filter(Boolean) // remove empty tokens

    let result: boolean | undefined
    let currentOp: '&&' | '||' | null = null

    for (const token of tokens) {
      if (token === '&&' || token === '||') {
        currentOp = token
        continue
      }

      // Operand: may start with "!"
      const isNegated = token.startsWith('!')
      const tag = isNegated ? token.slice(1) : token
      const value = isNegated ? !tagSet.has(tag) : tagSet.has(tag)

      if (result === undefined) {
        result = value // first operand
      } else if (currentOp === '&&') {
        result = result && value
      } else if (currentOp === '||') {
        result = result || value
      } else {
        // Should never happen if the input is well-formed
        console.error(`Unexpected token "${token}"`)
        result = false // fallback to false
      }
    }

    return result ?? false // default to false if empty
  }

  // Regex: %if[condition] ... endif%
  const blockPattern = /%if\[(.*?)\]([\s\S]*?)endif%/g // non-greedy so it stops at first endif%

  // Replace every block with either its body (if true) or ‘‘.
  return prompt.replace(blockPattern, (_, cond: string, body: string) => (evalCondition(cond) ? body : ''))
}
